import React, { useEffect, useState, useRef } from 'react';
import useGameBase from '../../hooks/useGameBase';

function TimerBar({ startTime, timeLimit, active, color = 'bg-yellow-400' }) {
  const [pct, setPct] = useState(0);
  const [remaining, setRemaining] = useState(timeLimit || 0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!active || !startTime || !timeLimit) {
      setPct(0);
      setRemaining(timeLimit || 0);
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const rem = Math.max(0, Math.ceil(timeLimit - elapsed));
      const p = Math.min(elapsed / timeLimit, 1);
      setRemaining(rem);
      setPct(p);
      if (p >= 1) clearInterval(intervalRef.current);
    };
    tick(); // run immediately so bar appears without a 1000ms delay
    intervalRef.current = setInterval(tick, 1000);
    return () => clearInterval(intervalRef.current);
  }, [active, startTime, timeLimit]);

  return (
    <div className="w-full mt-2 mb-1">
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
        <div
          className={`h-full ${color} transition-none`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
      <div className="text-xs text-right text-gray-500 dark:text-gray-400 mt-0.5">{remaining}s left</div>
    </div>
  );
}

export default function TruthOrDareGame({ roomId, isHost, gameState, gamePlayers = [], user = {}, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });
  const [hasVoted, setHasVoted] = useState(false);

  const phase = gameState ? gameState.phase : 'choosing';
  const players = gameState ? (gameState.players || []) : [];
  const scores = gameState ? (gameState.scores || {}) : {};
  const round = gameState ? (gameState.round || 1) : 1;
  const rounds = gameState ? (gameState.rounds || 10) : 10;
  const turnIndex = gameState ? (gameState.turnIndex || 0) : 0;
  const activePlayer = players[turnIndex] || null;
  const isActivePlayer = activePlayer && user && activePlayer.socketId === user.socketId;
  const currentCard = gameState ? gameState.currentCard : null;
  const votes = gameState ? (gameState.votes || {}) : {};
  const myVote = votes[user && user.socketId];
  const results = gameState ? (gameState.results || []) : [];
  const lastResult = results.length > 0 ? results[results.length - 1] : null;

  // Reset hasVoted when phase changes to voting
  useEffect(() => {
    if (phase === 'voting') setHasVoted(false);
  }, [phase, round]);

  const dispatch = async (action) => {
    if (typeof onGameAction === 'function') onGameAction(action);
    else await submitAction(action);
  };

  const pickType = (choice) => dispatch({ type: 'pickType', choice });
  const markDone = () => dispatch({ type: 'done' });
  const skipTurn = () => dispatch({ type: 'skip' });
  const vote = (v) => {
    if (hasVoted || myVote) return;
    setHasVoted(true);
    dispatch({ type: 'vote', vote: v });
  };

  // Count how many eligible voters have voted
  const voters = players.filter(p => p.socketId !== (activePlayer && activePlayer.socketId));
  const votedCount = voters.filter(p => votes[p.socketId]).length;

  const cardBg = currentCard && currentCard.type === 'truth'
    ? 'bg-blue-50 dark:bg-blue-950 border-blue-400'
    : 'bg-orange-50 dark:bg-orange-950 border-orange-400';

  const cardLabel = currentCard && currentCard.type === 'truth' ? '🤔 TRUTH' : '🎯 DARE';
  const cardLabelColor = currentCard && currentCard.type === 'truth'
    ? 'text-blue-700 dark:text-blue-300'
    : 'text-orange-700 dark:text-orange-300';

  return (
    <div className="truth-or-dare-game flex flex-col items-center min-h-full bg-gray-50 dark:bg-gray-950 p-2">
      <div className="w-full max-w-lg">

        {/* Header: round info + active player */}
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Round {round} of {rounds}
          </div>
          {activePlayer && phase !== 'finished' && (
            <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              {isActivePlayer ? "⭐ Your turn!" : `🎮 ${activePlayer.username || activePlayer.socketId}'s turn`}
            </div>
          )}
        </div>

        {/* ── CHOOSING phase ── */}
        {phase === 'choosing' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-5 text-center">
            {isActivePlayer ? (
              <>
                <p className="text-gray-700 dark:text-gray-300 mb-5 font-medium text-lg">What will it be?</p>
                <TimerBar
                  startTime={gameState && gameState.startTime}
                  timeLimit={30}
                  active={true}
                  color="bg-indigo-400"
                />
                <div className="flex gap-4 mt-4 justify-center">
                  <button
                    onClick={() => pickType('truth')}
                    className="flex-1 py-6 text-xl font-bold rounded-xl bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white shadow transition-colors"
                  >
                    🤔 Truth
                  </button>
                  <button
                    onClick={() => pickType('dare')}
                    className="flex-1 py-6 text-xl font-bold rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow transition-colors"
                  >
                    🎯 Dare
                  </button>
                </div>
                <button
                  onClick={skipTurn}
                  className="mt-4 text-sm text-gray-400 dark:text-gray-500 underline"
                >
                  Skip my turn
                </button>
              </>
            ) : (
              <div className="py-8 text-gray-500 dark:text-gray-400 text-lg">
                <div className="mb-2 animate-pulse">⏳</div>
                <div>Waiting for <span className="font-semibold text-gray-700 dark:text-gray-200">{activePlayer ? (activePlayer.username || activePlayer.socketId) : '...'}</span> to choose…</div>
                <TimerBar
                  startTime={gameState && gameState.startTime}
                  timeLimit={30}
                  active={true}
                  color="bg-indigo-400"
                />
              </div>
            )}
          </div>
        )}

        {/* ── PERFORMING phase ── */}
        {phase === 'performing' && currentCard && (
          <div className={`rounded-xl shadow border-2 p-5 ${cardBg}`}>
            <div className={`text-center text-sm font-bold uppercase tracking-widest mb-2 ${cardLabelColor}`}>{cardLabel}</div>
            <div className="text-center text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 leading-snug">
              {currentCard.text}
            </div>
            <TimerBar
              startTime={gameState && gameState.startTime}
              timeLimit={gameState && gameState.timeLimit}
              active={true}
              color={currentCard.type === 'truth' ? 'bg-blue-500' : 'bg-orange-500'}
            />
            {isActivePlayer ? (
              <div className="flex gap-3 mt-4 justify-center">
                <button
                  onClick={markDone}
                  className="flex-1 py-3 text-base font-bold rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 text-white shadow transition-colors"
                >
                  ✓ Done
                </button>
                <button
                  onClick={skipTurn}
                  className="px-5 py-3 text-base font-bold rounded-lg bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 shadow transition-colors"
                >
                  Skip
                </button>
              </div>
            ) : (
              <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Watching <span className="font-semibold text-gray-700 dark:text-gray-200">{activePlayer ? (activePlayer.username || activePlayer.socketId) : '?'}</span>…
              </div>
            )}
          </div>
        )}

        {/* ── VOTING phase ── */}
        {phase === 'voting' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-5">
            {/* Show the card as a reminder */}
            {currentCard && (
              <div className={`rounded-lg border-2 p-3 mb-4 ${cardBg}`}>
                <div className={`text-xs font-bold uppercase tracking-widest mb-1 ${cardLabelColor}`}>{cardLabel}</div>
                <div className="text-gray-900 dark:text-gray-100 font-medium">{currentCard.text}</div>
              </div>
            )}
            <TimerBar
              startTime={gameState && gameState.startTime}
              timeLimit={15}
              active={phase === 'voting'}
              color="bg-purple-400"
            />
            {isActivePlayer ? (
              <div className="text-center py-4 text-gray-600 dark:text-gray-300">
                <div className="text-2xl mb-2">🗳️</div>
                <div>Waiting for votes… <span className="font-semibold">{votedCount}/{voters.length}</span> in</div>
              </div>
            ) : (
              <div className="mt-2">
                <div className="text-center font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Did <span className="text-indigo-600 dark:text-indigo-400">{activePlayer ? (activePlayer.username || activePlayer.socketId) : '?'}</span> complete it?
                </div>
                {myVote || hasVoted ? (
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    You voted <strong>{myVote === 'up' ? '👍' : '👎'}</strong> — waiting for others… ({votedCount}/{voters.length})
                  </div>
                ) : (
                  <div className="flex gap-4 justify-center mt-2">
                    <button
                      onClick={() => vote('up')}
                      className="flex-1 py-5 text-3xl rounded-xl bg-green-100 dark:bg-green-900 hover:bg-green-200 dark:hover:bg-green-800 border-2 border-green-400 active:scale-95 transition-transform"
                    >
                      👍
                    </button>
                    <button
                      onClick={() => vote('down')}
                      className="flex-1 py-5 text-3xl rounded-xl bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 border-2 border-red-400 active:scale-95 transition-transform"
                    >
                      👎
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── REVEAL phase ── */}
        {phase === 'reveal' && lastResult && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-5">
            <div className="text-center mb-3">
              {lastResult.skipped ? (
                <div className="text-lg font-bold text-gray-500 dark:text-gray-400">
                  {lastResult.activeUsername || 'Player'} skipped their turn.
                </div>
              ) : (
                <>
                  <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                    {lastResult.activeUsername || 'Player'} earned <span className="text-green-600 dark:text-green-400">+{lastResult.pointsEarned} point{lastResult.pointsEarned !== 1 ? 's' : ''}</span>!
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    👍 {lastResult.upVotes} &nbsp; 👎 {lastResult.downVotes}
                  </div>
                </>
              )}
            </div>

            {/* Individual votes */}
            {!lastResult.skipped && Array.isArray(lastResult.voteDetails) && lastResult.voteDetails.length > 0 && (
              <div className="space-y-1 mb-3">
                {lastResult.voteDetails.map(v => (
                  <div key={v.socketId} className="flex items-center justify-between px-3 py-1.5 rounded bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-700 dark:text-gray-200">{v.username || v.socketId}</span>
                    <span className="text-base">{v.vote === 'up' ? '👍' : '👎'}</span>
                  </div>
                ))}
              </div>
            )}

            <TimerBar
              startTime={gameState && gameState.startTime}
              timeLimit={8}
              active={true}
              color="bg-gray-400"
            />
            <div className="text-center text-xs text-gray-400 dark:text-gray-500 mt-1">Next turn starting soon…</div>
          </div>
        )}

        {/* ── FINISHED phase ── */}
        {phase === 'finished' && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-5">
            <div className="text-center text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">🏆 Game Over!</div>
            {gameState && gameState.result && gameState.result.winnerNames && (
              <div className="text-center text-indigo-600 dark:text-indigo-400 font-semibold mb-4">
                {gameState.result.outcome === 'tie' ? 'It\'s a tie — ' : 'Winner: '}
                {gameState.result.winnerNames}
              </div>
            )}
            <div className="space-y-2">
              {players
                .slice()
                .sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0))
                .map((p, i) => (
                  <div
                    key={p.socketId}
                    className={`flex items-center justify-between px-3 py-2 rounded border ${p.socketId === (user && user.socketId) ? 'bg-indigo-50 dark:bg-indigo-950 border-indigo-300' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-400 w-5">#{i + 1}</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200">{p.username || p.socketId}</span>
                      {p.socketId === (user && user.socketId) && <span className="text-xs text-indigo-500">(you)</span>}
                    </div>
                    <span className="font-bold text-gray-800 dark:text-gray-200">{scores[p.socketId] || 0} pts</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* ── Scoreboard (shown during play) ── */}
        {phase !== 'finished' && players.length > 0 && (
          <div className="mt-4 bg-white dark:bg-gray-900 rounded-xl shadow p-3">
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Scores</div>
            <div className="flex flex-wrap gap-2">
              {players
                .slice()
                .sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0))
                .map((p) => (
                  <div
                    key={p.socketId}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm border ${p.socketId === (activePlayer && activePlayer.socketId) ? 'bg-yellow-100 dark:bg-yellow-900 border-yellow-400 font-semibold' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                  >
                    <span className={`${p.socketId === (user && user.socketId) ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                      {p.username || p.socketId}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{scores[p.socketId] || 0}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

TruthOrDareGame.Guide = function TruthOrDareGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2 text-gray-900 dark:text-gray-100">Truth or Dare</h3>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-300">
        <li>Players take turns being in the hot seat.</li>
        <li>The active player picks <strong>Truth</strong> (answer a question honestly) or <strong>Dare</strong> (complete a challenge).</li>
        <li>When you're done, press <strong>Done ✓</strong>. All other players then vote 👍 or 👎.</li>
        <li>Your score = the number of 👍 votes you receive that round.</li>
        <li>You can <strong>Skip</strong> your turn at any time, but you earn no points for it.</li>
        <li>The player with the most points when all rounds are played wins!</li>
      </ul>
    </div>
  );
};
